/**
 * Utility Helpers
 * Mikhmel Cornerstone4D DICOM Viewer Pro v5
 * [UPGRADED: WebWorkers, Memory Protection, Hardware Diagnostics]
 */

// [10.3.2] Заслон от служебной болтовни. Правило — в toastPolicy.js, файле без
// импортов; здесь только точка, через которую проходят ВСЕ всплывающие окна.
import { judgeToast } from '../core/toastPolicy.js';

/**
 * Format file size
 */
export function formatFileSize(bytes) {
  if (typeof bytes !== 'number' || isNaN(bytes) || bytes < 0) return '0 B'; // [PRO-FEATURE] Защита от NaN и отрицательных
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Format DICOM date (YYYYMMDD -> DD.MM.YYYY)
 */
export function formatDicomDate(dateStr) {
  if (!dateStr || dateStr.length < 8) return dateStr || '';
  return `${dateStr.slice(6, 8)}.${dateStr.slice(4, 6)}.${dateStr.slice(0, 4)}`;
}

/**
 * Format DICOM time (HHMMSS.fff -> HH:MM:SS)
 */
export function formatDicomTime(timeStr) {
  if (!timeStr || timeStr.length < 6) return timeStr || '';
  return `${timeStr.slice(0, 2)}:${timeStr.slice(2, 4)}:${timeStr.slice(4, 6)}`;
}

/**
 * Parse DICOM patient name (Last^First^Middle -> First Last)
 */
export function formatPatientName(name) {
  if (!name) return '';
  // [11.1.20] ДВЕ ПОЧИНКИ по замечанию владельца «ФИО берётся на латыни».
  //
  // 1. ГРУППЫ. Имя в DICOM (VR PN) состоит из трёх групп через «=»:
  //    алфавитная = идеографическая = фонетическая. Русские ПАКС часто
  //    пишут в первую транслит (MIKHMEL^ANDREY), а кириллицу — во вторую
  //    или третью. Мы всегда брали первую, поэтому в бланк ехала латиница.
  //    Теперь выбирается группа, где есть кириллица; если её нет нигде —
  //    первая непустая (транслит; из файла взять больше нечего, поле в
  //    форме правится руками).
  // 2. ПОРЯДОК И ОТЧЕСТВО. Внутри группы порядок компонент —
  //    Фамилия^Имя^Отчество. Прежний код возвращал `parts[1] parts[0]`:
  //    западный порядок И ПОТЕРЯ ОТЧЕСТВА — «АНДРЕЙ МИХМЕЛЬ» вместо
  //    «МИХМЕЛЬ АНДРЕЙ ВЯЧЕСЛАВОВИЧ». В русском заключении так нельзя.
  const группы = String(name).split('=').map((г) => г.trim()).filter(Boolean);
  const кириллица = /[А-Яа-яЁё]/;
  const выбранная = группы.find((г) => кириллица.test(г)) || группы[0] || '';
  return выбранная.split('^').map((ч) => ч.trim()).filter(Boolean).join(' ').trim();
}

/**
 * Debounce function
 */
export function debounce(fn, delay = 250) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

/**
 * Throttle function
 */
export function throttle(fn, limit = 100) {
  let inThrottle;
  return function (...args) {
    if (!inThrottle) {
      fn.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Generate unique ID
 */
export function uid(prefix = '') {
  return `${prefix}${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Deep clone
 */
export function deepClone(obj) {
  // [PRO-FEATURE] Поддержка бинарных массивов DICOM (PixelData).
  // Обычный JSON.parse ломается на TypedArrays, вызывая лаги памяти.
  if (ArrayBuffer.isView(obj) || obj instanceof ArrayBuffer) return obj.slice(0);
  return (typeof structuredClone === 'function') ? structuredClone(obj) : JSON.parse(JSON.stringify(obj)); // [FIX-IMP7]
}

/**
 * Download blob as file
 */
export function downloadBlob(blob, filename) {
  if (!blob || blob.size === 0) return; // [PRO-FEATURE] Блокировка сохранения пустых (битых) файлов
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000); // [FIX-P2-7] отложенный revoke
}

/**
 * Canvas to blob
 */
export function canvasToBlob(canvas, type = 'image/png', quality = 0.95) {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Export viewport screenshot
 */
export async function screenshotViewport(viewportElement, filename = 'screenshot.png') {
  try {
    const canvas = viewportElement.querySelector('canvas');
    if (!canvas) return;
    const blob = await canvasToBlob(canvas);
    downloadBlob(blob, filename);
  } catch (e) { console.warn(`[helpers] screenshotViewport failed:`, e.message);  }
}

/**
 * Array statistics
 * [ПРОКАЧАНО]: Добавлена защита от NaN и Infinity (бывают в поврежденных файлах DICOM),
 * при этом сохранен ваш высокопроизводительный цикл `for` (никаких filter/reduce).
 */
export function arrayStats(arr) {
  if (!arr || arr.length === 0) return { min: 0, max: 0, mean: 0, std: 0 };
  let min = Infinity, max = -Infinity, sum = 0;
  let validCount = 0; 
  for (let i = 0; i < arr.length; i++) {
    if (Number.isNaN(arr[i])) continue; // Игнорируем битые пиксели, не ломая расчеты
    if (arr[i] < min) min = arr[i];
    if (arr[i] > max) max = arr[i];
    sum += arr[i];
    validCount++;
  }
  if (validCount === 0) return { min: 0, max: 0, mean: 0, std: 0 };
  
  const mean = sum / validCount;
  let sumSq = 0;
  for (let i = 0; i < arr.length; i++) {
    if (Number.isNaN(arr[i])) continue;
    sumSq += (arr[i] - mean) ** 2;
  }
  const std = Math.sqrt(sumSq / validCount);
  return { min, max, mean, std };
}

/**
 * Clamp value
 */
export function clamp(val, min, max) {
  return Math.max(min, Math.min(max, val));
}

/**
 * Linear interpolation
 */
export function lerp(a, b, t) {
  return a + (b - a) * t;
}

/**
 * Map value from one range to another
 */
export function mapRange(val, inMin, inMax, outMin, outMax) {
  return outMin + ((val - inMin) / (inMax - inMin)) * (outMax - outMin);
}

/**
 * Check if browser supports required features
 */
export function checkBrowserSupport() {
  return {
    webgl2: !!document.createElement('canvas').getContext('webgl2'),
    webgpu: !!navigator.gpu,
    sharedArrayBuffer: typeof SharedArrayBuffer !== 'undefined',
    offscreenCanvas: typeof OffscreenCanvas !== 'undefined',
    webWorker: typeof Worker !== 'undefined',
    wasm: typeof WebAssembly !== 'undefined',
    structuredClone: typeof structuredClone !== 'undefined',
  };
}

/**
 * Display toast notification — [FIX-C5] toast container для стекинга
 */
function _getToastContainer() {
  let c = document.getElementById('toast-container');
  if (!c) {
    c = document.createElement('div');
    c.id = 'toast-container';
    c.style.cssText = 'display:flex;flex-direction:column-reverse;gap:8px;pointer-events:none;';
    // [9.0.2] Раньше контейнер сам вставал в правый нижний угол с z-index 10001
    // и воевал за это место с шестью другими панелями. Теперь он — один из
    // жильцов общего столбца, и перекрыть его никто не может.
    try {
      // Загружаем лениво: helpers.js — низкоуровневый и не должен тянуть за
      // собой UI при первом же импорте.
      import('../ui/notifyHost.js')
        .then(m => m.mountNotice(c, m.SLOT.INFO))
        .catch(() => {
          c.style.cssText += 'position:fixed;bottom:10px;right:10px;z-index:2147480000;';
          document.body.appendChild(c);
        });
    } catch (e) {
      c.style.cssText += 'position:fixed;bottom:10px;right:10px;z-index:2147480000;';
      document.body.appendChild(c);
    }
    if (!c.parentNode) document.body.appendChild(c);
  }
  return c;
}

/**
 * [10.19.1] Единая реакция на отказ от загрузки каталога — для ОБОИХ путей.
 *
 * Беда 10.19.0 была починена только на пути showDirectoryPicker; путь поля
 * <input webkitdirectory> — единственный на телефоне — на отказ по-прежнему
 * молчал: ни сообщения, ни освобождения окна сравнения, а окно «Читаю список
 * файлов…» с бегущими секундами оставалось на экране навсегда, потому что
 * гасит его только handleFiles, который при отказе не зовётся. Теперь оба
 * пути зовут одно место — это.
 */
export function notifyFolderLoadCancelled(текст) {
  try { clearInterval(window.__обходТаймер); window.__обходТаймер = null; } catch (e) { /* */ }
  try { window.MikhmelViewer?.hideLoadingOverlay?.(); } catch (e) { /* */ }
  try {
    showToast(текст || 'Загрузка отменена — ничего не открыто. Каталог был прочитан, но выбор прореживания отменён',
      'warning', 6000);
  } catch (e) { /* */ }
  try { window.MikhmelViewer?.compareOpenCancelled?.(); } catch (e) { /* */ }
}

/**
 * [10.19.1] Предупреждение о прореживании — с настоящим шагом, из одного
 * места для обоих путей. Раньше текст всегда говорил «взяты через один»,
 * даже при «каждом 3-м», и жил только на пути showDirectoryPicker: телефон
 * прореживал молча.
 */
export function notifyDecimation(взято, всего) {
  const шаг = взято > 0 ? Math.max(2, Math.round(всего / взято)) : 2;
  try {
    showToast(`Загружается ${взято} файлов из ${всего} — с прореживанием: взят каждый ${шаг}-й срез по всему каталогу. `
      + 'Измерения объёма и карта разницы будут занижены', 'warning', 9000);
  } catch (e) { /* */ }
}

/**
 * Показать всплывающее уведомление
 * @param {string} message — текст сообщения
 * @param {'info'|'success'|'warning'|'error'} [type='info']
 * @param {number} [duration=3000] — время показа в мс
 */
export function showToast(message, type = 'info', duration = 3000, повод = '') {
  // [10.3.2] Служебное — в журнал, а не на экран. Заслон стоит здесь, в
  // единственной общей точке: по одному вызову такие сообщения убирать
  // бесполезно, они написаны в разных файлах и вылезают снова.
  const verdict = judgeToast(message, type, повод); // [10.19.44] повод «прямой-ответ» заслон не глушит
  if (!verdict.allow) {
    try { console.debug('[toast скрыт]', verdict.why, '—', message); } catch (e) { /* */ }
    return;
  }

  const container = _getToastContainer();
  
  // [PRO-FEATURE] Anti-Spam защита. Если вылезло 10 ошибок загрузки слоев,
  // старые тосты удаляются, чтобы не перекрыть весь экран и не повесить DOM.
  if (container.childElementCount > 4) {
    container.firstElementChild?.remove();
  }

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;
  toast.textContent = message;
  // [10.19.1] Тост НЕ ловит нажатия (pointer-events: none).
  //
  // Поймано осмотром кнопок на телефоне 412×915 в режиме сравнения: тост,
  // висящий у нижнего края, накрывал кнопки масштаба окна, ✕ карточки выбора и
  // кнопку «Карта разницы» — и они на время его показа становились глухими.
  // На настольном экране это незаметно, на телефоне тост занимает пол-экрана.
  // Закрывать тост нажатием было нельзя и раньше (обработчика нет), он гаснет
  // сам по времени, — значит ловить нажатия ему незачем вовсе.
  toast.style.cssText = `
    padding: 12px 20px; border-radius: 8px; color: #fff;
    font-size: 14px; pointer-events: none;
    opacity: 0; transform: translateY(10px);
    transition: all 0.3s ease; max-width: 360px; word-break: break-word;
    background: ${type === 'error' ? '#ef4444' : type === 'success' ? '#22c55e' : type === 'warning' ? '#eab308' : '#3b82f6'};
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  `;
  container.appendChild(toast);
  requestAnimationFrame(() => {
    toast.style.opacity = '1';
    toast.style.transform = 'translateY(0)';
  });
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transform = 'translateY(10px)';
    setTimeout(() => toast.remove(), 300);
  }, duration);
}

// ══════════════════════════════════════════════════════════════
// XSS Protection — DICOM metadata может содержать произвольные строки
// ══════════════════════════════════════════════════════════════

const _escMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

/**
 * Экранирование HTML-спецсимволов (защита от XSS при innerHTML)
 * @param {string} str
 * @returns {string}
 */
export function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/[&<>"']/g, c => _escMap[c]);
}

/**
 * Tagged template для безопасного HTML с интерполяцией
 * Использование: safeHTML`<div>${untrustedValue}</div>`
 * @returns {string}
 */
export function safeHTML(strings, ...values) {
  return strings.reduce((out, str, i) =>
    out + str + (i < values.length ? escapeHtml(values[i]) : ''), '');
}


// ══════════════════════════════════════════════════════════════
// НОВЫЕ СУПЕР-ФИЧИ (INTEGRATED FOR CORNERSTONE 4D PRO)
// ══════════════════════════════════════════════════════════════

/**
 * [ФИЧА 1]: Inline WebWorker для тяжелой математики
 * DICOM вьювер часто зависает, когда нужно пересчитать LUT или гистограмму
 * для 2000 кадров. Эта функция берет вашу логику и выполняет её в параллельном
 * потоке ЦП, оставляя интерфейс идеально плавным (60 FPS).
 * @example
 * const stats = await runInWorker(arrayStats, heavyPixelDataArray);
 */
export function runInWorker(fn, data) {
  return new Promise((resolve, reject) => {
    if (typeof Worker === 'undefined') {
      try { resolve(fn(data)); } catch (e) { reject(e); }
      return;
    }
    const workerCode = `
      self.onmessage = function(e) {
        try {
          const result = (${fn.toString()})(e.data);
          self.postMessage({ success: true, result });
        } catch (err) {
          self.postMessage({ success: false, error: err.message });
        }
      };
    `;
    const blob = new Blob([workerCode], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));
    
    worker.onmessage = (e) => {
      e.data.success ? resolve(e.data.result) : reject(new Error(e.data.error));
      worker.terminate();
    };
    worker.onerror = (e) => { reject(e); worker.terminate(); };
    worker.postMessage(data);
  });
}

/**
 * [ФИЧА 2]: Диагностика железа (GPU + Memory)
 * Критически важно для техподдержки. Если у пользователя артефакты 3D-рендера
 * или вылет WebGL, вы вызываете эту функцию и шлете в лог точное название
 * видеокарты (например, "Intel HD 620") и объем занятой RAM.
 */
export function getSystemDiagnostics() {
  const diag = { gpu: 'unknown', memoryUsedMB: 'unknown' };
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('webgl2');
    if (gl) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      if (ext) diag.gpu = gl.getParameter(ext.UNMASKED_RENDERER_WEBGL);
    }
    if (performance && performance.memory) {
      diag.memoryUsedMB = Math.round(performance.memory.usedJSHeapSize / 1048576);
    }
  } catch (e) { console.debug("[helpers]", e.message); }
  return diag;
}

/**
 * [ФИЧА 3]: Умный реестр очистки памяти (FinalizationRegistry)
 * Идеальная замена setTimeout для отложенного revokeObjectURL.
 * Этот механизм автоматически уничтожает URL объекта в тот момент, когда
 * Garbage Collector браузера понимает, что Blob больше нигде не используется.
 */
const blobRegistry = typeof FinalizationRegistry !== 'undefined'
  ? new FinalizationRegistry((url) => URL.revokeObjectURL(url))
  : null;

export function createSmartBlobUrl(blob) {
  const url = URL.createObjectURL(blob);
  if (blobRegistry) blobRegistry.register(blob, url);
  return url;
}

/**
 * [11.1.18] спроситьТекст — замена prompt(). В настольной оболочке Electron
 * prompt() запрещён и падает «prompt() is not supported» — семь пунктов
 * Enterprise-панели и сохранение VRT-пресета молча умирали (доктрина уже
 * записана в expiryShift.js: «окно prompt() запрещено… поэтому своё окно»).
 * Здесь то же своё окно, но общее: заголовок, поле (или textarea), ОК/Отмена,
 * Enter/Esc. Возвращает Promise<string|null>; null — человек передумал.
 * @param {string} заголовок
 * @param {{поУмолчанию?:string, многострочно?:boolean, подсказка?:string}} [нстр]
 */
export function спроситьТекст(заголовок, нстр = {}) {
  const { поУмолчанию = '', многострочно = false, подсказка = '' } = нстр;
  return new Promise((resolve) => {
    const о = document.createElement('div');
    о.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,.6);z-index:999999;display:flex;align-items:center;justify-content:center;';
    const окно = document.createElement('div');
    окно.style.cssText = 'min-width:min(420px,92vw);background:var(--bg-panel,#161628);border:1px solid var(--border,#2a2a40);border-radius:10px;padding:16px;color:var(--text-primary,#e0e0e0);font:13px/1.5 system-ui;box-shadow:0 8px 32px rgba(0,0,0,.6);';
    const заг = document.createElement('div');
    заг.textContent = заголовок;
    заг.style.cssText = 'font-weight:600;margin-bottom:10px;';
    const поле = document.createElement(многострочно ? 'textarea' : 'input');
    if (многострочно) поле.rows = 5;
    поле.value = поУмолчанию;
    поле.style.cssText = 'width:100%;box-sizing:border-box;padding:7px 10px;background:var(--bg-input,#0e0e1c);border:1px solid var(--border,#2a2a40);border-radius:6px;color:inherit;font:inherit;resize:vertical;';
    const низ = document.createElement('div');
    низ.style.cssText = 'display:flex;gap:8px;justify-content:flex-end;margin-top:12px;';
    const мк = (текст, главная) => {
      const b = document.createElement('button');
      b.textContent = текст;
      b.style.cssText = 'padding:6px 16px;border-radius:6px;border:1px solid var(--border,#2a2a40);cursor:pointer;font:inherit;' +
        (главная ? 'background:var(--accent,#3b82f6);color:#fff;border-color:transparent;' : 'background:transparent;color:inherit;');
      return b;
    };
    const ок = мк('ОК', true), нет = мк('Отмена', false);
    const конец = (v) => { try { о.remove(); } catch (e) { /* */ } document.removeEventListener('keydown', наКлавишу, true); resolve(v); };
    const наКлавишу = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); конец(null); }
      else if (e.key === 'Enter' && (!многострочно || e.ctrlKey)) { e.stopPropagation(); конец(поле.value); }
    };
    ок.addEventListener('click', () => конец(поле.value));
    нет.addEventListener('click', () => конец(null));
    о.addEventListener('click', (e) => { if (e.target === о) конец(null); });
    document.addEventListener('keydown', наКлавишу, true);
    низ.append(нет, ок);
    окно.append(заг);
    if (подсказка) { const п = document.createElement('div'); п.textContent = подсказка; п.style.cssText = 'color:var(--text-dim,#8a8aa0);font-size:11px;margin-bottom:8px;white-space:pre-wrap;'; окно.append(п); }
    окно.append(поле, низ);
    о.append(окно);
    document.body.append(о);
    поле.focus(); if (!многострочно) поле.select();
  });
}
